<template>
  <v-container fluid class="signup-page d-flex align-center justify-center fill-height">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="10" md="8" lg="7" xl="5">
        <v-card class="signup-card pa-5 pa-sm-8 pa-md-10" elevation="6" rounded="xl">
          <!-- ✅ Success State -->
          <v-layout
            v-if="signUpSuccess"
            align-center
            justify-center
            column
            fill-height
          >
            <v-img
              contain
              :src="brandConfig.logos.inline"
              max-height="48"
              max-width="220"
              class="mb-8"
              :alt="`${brandDisplayName} logo`"
            />
            <div class="verification-icon d-flex align-center justify-center mb-5">
              <v-icon color="primary" size="42">mdi-email-check-outline</v-icon>
            </div>
            <h1 class="verification-title text-center mb-3">Check your email</h1>
            <p class="verification-copy text-center mb-2">
              We sent a verification link to
            </p>
            <p class="verification-email text-center font-weight-medium mb-5">
              {{ user.email }}
            </p>
            <v-alert
              v-if="resendSucceeded"
              dense
              text
              type="success"
              class="verification-alert mb-5"
              role="status"
            >
              A new verification email is on its way.
            </v-alert>
            <v-alert
              v-if="resendError"
              dense
              text
              type="error"
              class="verification-alert mb-5"
              role="alert"
            >
              {{ resendError }}
            </v-alert>
            <p class="verification-help text-center mb-5">
              Open the link in the email to finish setting up your {{ brandDisplayName }} account.
              If it is not in your inbox, check your spam folder or send another email.
            </p>
            <v-btn
              block
              large
              color="primary"
              class="v-btn-with-radius mb-3"
              :loading="isResending"
              :disabled="isResending"
              @click="resend"
            >
              Resend verification email
            </v-btn>
            <v-btn block large text color="primary" to="/login">
              Back to sign in
            </v-btn>
          </v-layout>

          <!-- ❌ Signup Form -->
          <v-layout column fill-height v-else>
            <!-- 🏷️ Dynamic Logo -->
            <div class="text-center mb-7">
              <img
                :src="brandConfig.logos.inline"
                class="signup-logo"
                :alt="`${brandDisplayName} logo`"
              />
            </div>

            <!-- Header -->
            <div class="text-center mb-7">
              <h1 class="signup-title font-weight-bold mb-2">Create your account</h1>
              <p class="signup-subtitle mb-0">
                Get started with {{ brandDisplayName }}
              </p>
            </div>

            <!-- Form Fields -->
            <v-card-text class="pa-0 signup-form">
              <v-row dense>
                <v-col cols="12" sm="6" class="pr-sm-2">
                  <v-text-field
                    ref="firstName"
                    v-model="user.firstName"
                    label="First Name"
                    outlined
                    color="primary"
                    :error-messages="firstNameErrors"
                    autocomplete="given-name"
                    @blur="$v.user.firstName.$touch()"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="pl-sm-2">
                  <v-text-field
                    ref="lastName"
                    v-model="user.lastName"
                    label="Last Name"
                    outlined
                    color="primary"
                    :error-messages="lastNameErrors"
                    autocomplete="family-name"
                    @blur="$v.user.lastName.$touch()"
                  />
                </v-col>
              </v-row>

              <v-text-field
                ref="email"
                v-model="user.email"
                label="Email address"
                outlined
                color="primary"
                :error-messages="emailErrors"
                autocomplete="email"
                inputmode="email"
                @blur="$v.user.email.$touch()"
              />

              <v-row dense>
                <v-col cols="12" sm="6" class="pr-sm-2">
                  <v-text-field
                    ref="password"
                    v-model="user.password"
                    label="Password"
                    outlined
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                    color="primary"
                    :error-messages="passwordErrors"
                    autocomplete="new-password"
                    hint="Use at least 4 characters"
                    persistent-hint
                    @click:append="showPassword = !showPassword"
                    @blur="$v.user.password.$touch()"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="pl-sm-2">
                  <v-text-field
                    ref="confirmPassword"
                    v-model="user.confirmPassword"
                    label="Confirm Password"
                    outlined
                    :type="showConfirmPassword ? 'text' : 'password'"
                    :append-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                    color="primary"
                    :error-messages="confirmPasswordErrors"
                    autocomplete="new-password"
                    @click:append="showConfirmPassword = !showConfirmPassword"
                    @blur="$v.user.confirmPassword.$touch()"
                    @keyup.enter="submit"
                  />
                </v-col>
              </v-row>
            </v-card-text>

            <!-- Actions -->
            <div class="security-check d-flex justify-center mt-3 mb-6">
                <div id="cf-turnstile"></div>
            </div>
            <v-btn
              block
              color="primary"
              large
              class="v-btn-with-radius signup-submit"
              :loading="isSubmitting"
              :disabled="!turnstileToken || isSubmitting"
              @click="submit"
            >
              Create account
            </v-btn>
            <div class="signin-prompt text-center mt-6">
              <span>Already have an account?</span>
              <v-btn
                text
                color="primary"
                class="signin-link px-2"
                to="/login"
              >
                Sign in
              </v-btn>
            </div>
          </v-layout>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import signUpService from "./service.js";
import { mapGetters } from "vuex";
const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  sameAs,
} = require("vuelidate/lib/validators");

export default {
  name: "signUp",
  mixins: [validationMixin],

  data() {
    return {
      user: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        confirmPassword: null,
        acceptedTerms: false,
        ReferralCode: null,
        plan: null,
        forceVerify: false,
      },
      turnstileToken: null,
      turnstileWidgetId: null,
      signUpSuccess: false,
      isResending: false,
      resendSucceeded: false,
      resendError: "",
      isSubmitting: false,
      showPassword: false,
      showConfirmPassword: false,
    };
  },
  mounted() {
    this.loadTurnstile();
  },
  created() {
    // query params / routes
    if (this.$route.query) {
      this.user.ReferralCode = this.$route.query.referralCode;
      this.user.email = this.$route.query.email;
      this.user.plan = this.$route.query.plan;
      this.user.source = this.$route.query.source;
    }
    if (this.$route.params.email) {
      this.user.email = this.$route.params.email;
      this.user.firstName = this.$route.params.firstName;
      this.user.lastName = this.$route.params.lastName;
      this.user.forceVerify = this.$route.params.verified;
    }
  },

  computed: {
    ...mapGetters("brand", ["currentBrand", "brandConfig"]),

    // 🎨 Brand-Based Product Name
    brandDisplayName() {
      if (this.currentBrand === "storesync") return "StoreSync POS";
      if (this.currentBrand === "autorepair") return "AutoRepairDesk";
      if (this.currentBrand === "repairnest") return "RepairNest POS";
      return "Our Platform";
    },

    brandPrimaryColor() {
      return this.brandConfig && this.brandConfig.theme
        ? this.brandConfig.theme.primary
        : undefined;
    },

    // ❌ Validation error helpers (unchanged)
    firstNameErrors() {
      const e = [];
      if (!this.$v.user.firstName.$dirty) return e;
      if (!this.$v.user.firstName.required) e.push("First name is required.");
      if (!this.$v.user.firstName.minLength)
        e.push("Must be at least 2 characters.");
      return e;
    },
    lastNameErrors() {
      const e = [];
      if (!this.$v.user.lastName.$dirty) return e;
      if (!this.$v.user.lastName.minLength)
        e.push("Must be at least 1 character.");
      return e;
    },
    emailErrors() {
      const e = [];
      if (!this.$v.user.email.$dirty) return e;
      if (!this.$v.user.email.required) e.push("Email is required.");
      if (!this.$v.user.email.email) e.push("Invalid email format.");
      return e;
    },
    passwordErrors() {
      const e = [];
      if (!this.$v.user.password.$dirty) return e;
      if (!this.$v.user.password.required) e.push("Password is required.");
      if (!this.$v.user.password.minLength)
        e.push("Must be at least 4 characters long.");
      return e;
    },
    confirmPasswordErrors() {
      const e = [];
      if (!this.$v.user.confirmPassword.$dirty) return e;
      if (!this.$v.user.confirmPassword.sameAsPassword)
        e.push("Passwords do not match.");
      return e;
    },
  },

  methods: {
    loadTurnstile() {
      var self = this;

      if (window.turnstile) {
        if (self.turnstileWidgetId == null) {
          self.renderTurnstile();
        }
        return;
      }

      if (document.querySelector('script[data-turnstile="true"]')) {
        return;
      }

      var s = document.createElement("script");
      s.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.setAttribute("data-turnstile", "true");
      s.onload = function () {
        if (self.turnstileWidgetId == null) {
          self.renderTurnstile();
        }
      };
      document.head.appendChild(s);
    },

    renderTurnstile() {
      var self = this;
      var siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY;

      if (!siteKey) {
        console.error("Missing VUE_APP_TURNSTILE_SITE_KEY");
        return;
      }

      self.turnstileWidgetId = window.turnstile.render("#cf-turnstile", {
        sitekey: siteKey,
        callback: function (token) {
          self.turnstileToken = token;
        },
        "expired-callback": function () {
          self.turnstileToken = null;
          if (self.turnstileWidgetId != null) {
            window.turnstile.reset(self.turnstileWidgetId);
          }
        },
        "timeout-callback": function () {
          self.turnstileToken = null;
          if (self.turnstileWidgetId != null) {
            window.turnstile.reset(self.turnstileWidgetId);
          }
          self.$toasted.error("Security check timed out. Please try again.", {
            position: "top-right",
            duration: 3500,
          });
        },
        "error-callback": function (code) {
          self.turnstileToken = null;
          console.error("Turnstile error:", code);
          self.$toasted.error(
            "Security check failed. Please refresh and try again.",
            {
              position: "top-right",
              duration: 3500,
            }
          );
        },
      });
    },
    normalizeEmail() {
      var e = this.user.email == null ? "" : String(this.user.email);

      // trim + lowercase + remove any inner whitespace (optional)
      e = e.trim().toLowerCase();

      // if you want to be strict: remove spaces anywhere
      e = e.replace(/\s+/g, "");

      this.user.email = e;
    },
    async submit() {
      this.normalizeEmail();

      this.$v.$touch();

      if (this.$v.$invalid) {
        await this.$swal.fire({
          icon: "error",
          title: "Check the highlighted fields",
          text: "Complete the required information before creating your account.",
          confirmButtonText: "Review form",
          confirmButtonColor: this.brandPrimaryColor,
        });
        await this.$nextTick();
        const invalidField = [
          ["firstName", this.$v.user.firstName],
          ["lastName", this.$v.user.lastName],
          ["email", this.$v.user.email],
          ["password", this.$v.user.password],
          ["confirmPassword", this.$v.user.confirmPassword],
        ].find(([, validation]) => validation.$invalid);
        if (invalidField && this.$refs[invalidField[0]]) {
          this.$refs[invalidField[0]].focus();
        }
        return;
      }

      if (!this.turnstileToken) {
        this.$swal.fire({
          icon: "info",
          title: "Complete the security check",
          text: "Confirm that you are human before creating your account.",
          confirmButtonText: "Got it",
          confirmButtonColor: this.brandPrimaryColor,
        });
        return;
      }

      const payload = {
        ...this.user,
        turnstileToken: this.turnstileToken,
        appSource: this.currentBrand,
      };

      this.isSubmitting = true;
      signUpService
        .submit(payload, this.currentBrand)
        .then(() => {
          this.turnstileToken = null;

          if (this.$route.query.plan || this.user.forceVerify) {
            this.$toasted.success("User created.", {
              position: "top-right",
              duration: 3500,
            });

            this.$router.push({
              name: "newOrganization",
              params: { plan: this.$route.query.plan },
            });
          } else {
            this.signUpSuccess = true;
          }
        })
        .catch((error) => {
          this.turnstileToken = null;

          if (this.turnstileWidgetId != null && window.turnstile) {
            window.turnstile.reset(this.turnstileWidgetId);
          }

          this.$swal.fire({
            icon: "error",
            title: "We couldn't create your account",
            text:
              error?.response?.data?.message ||
              error?.data?.message ||
              "Please check your information and try again.",
            confirmButtonText: "Try again",
            confirmButtonColor: this.brandPrimaryColor,
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },

    resend() {
      if (this.isResending) return;

      this.isResending = true;
      this.resendSucceeded = false;
      this.resendError = "";
      signUpService
        .resendVerifyEmail({
          email: this.user.email,
          appSource: this.currentBrand, // 👈 cleaner
        }, this.currentBrand)
        .then(() => {
          this.resendSucceeded = true;
          this.resendError = "";
          this.$toasted.success(
            `Resent verification email to ${this.user.email}`,
            {
              position: "top-right",
              icon: "check",
              duration: 2000,
            }
          );
        })
        .catch((error) => {
          const apiMessage =
            error?.response?.data?.message || error?.data?.message || "";
          const exposesInternalProviderError =
            /credentials|smtp|nodemailer|\bplain\b|authentication/i.test(
              apiMessage
            );

          this.resendError = exposesInternalProviderError
            ? "We couldn't send another email right now. Please try again in a moment."
            : apiMessage ||
              "We couldn't send another email right now. Please try again in a moment.";
          this.$toasted.error(
            this.resendError,
            {
              position: "top-right",
              duration: 3500,
            }
          );
        })
        .finally(() => {
          this.isResending = false;
        });
    },
  },

  validations: {
    user: {
      firstName: { required, minLength: minLength(2) },
      lastName: { minLength: minLength(1) },
      email: { required, email },
      password: { required, minLength: minLength(4) },
      confirmPassword: { sameAsPassword: sameAs("password") },
    },
  },
};
</script>
<style lang="scss" scoped>
.signup-page {
  min-height: 100vh;
  padding: 32px 16px;
  background:
    radial-gradient(circle at 15% 15%, rgba(127, 127, 127, 0.08), transparent 32%),
    var(--brand-bg);
}
.signup-card {
  width: 100%;
  max-width: 720px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.signup-logo {
  display: inline-block;
  max-width: 220px;
  width: auto;
  height: 46px;
  object-fit: contain;
}
.signup-title {
  font-size: clamp(1.65rem, 4vw, 2rem);
  line-height: 1.2;
  color: var(--brand-text);
}
.signup-subtitle,
.signin-prompt {
  color: rgba(0, 0, 0, 0.64);
}
.signup-form {
  max-width: 620px;
  margin: 0 auto;
}
.security-check {
  min-height: 65px;
  overflow-x: auto;
}
.signup-submit {
  min-height: 48px;
  letter-spacing: 0.02em;
}
.signin-link {
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}
.verification-icon {
  width: 76px;
  height: 76px;
  border-radius: 24px;
  background: var(--brand-secondary);
}
.verification-title {
  font-size: 1.75rem;
  line-height: 1.25;
}
.verification-copy,
.verification-help {
  max-width: 520px;
  color: rgba(0, 0, 0, 0.66);
}
.verification-email {
  overflow-wrap: anywhere;
}
.verification-alert {
  width: 100%;
  max-width: 520px;
}
.theme--dark .signup-card {
  border-color: rgba(255, 255, 255, 0.12);
}
.theme--dark .signup-page {
  background:
    radial-gradient(circle at 15% 15%, rgba(255, 255, 255, 0.06), transparent 32%),
    var(--brand-text);
}
.theme--dark .signup-title {
  color: rgba(255, 255, 255, 0.96);
}
.theme--dark .signup-subtitle,
.theme--dark .signin-prompt {
  color: rgba(255, 255, 255, 0.7);
}
.theme--dark .verification-copy,
.theme--dark .verification-help {
  color: rgba(255, 255, 255, 0.7);
}
@media (max-width: 599px) {
  .signup-page {
    padding: 12px 4px;
    align-items: flex-start !important;
  }
  .signup-card {
    box-shadow: none !important;
  }
  .signup-logo {
    height: 40px;
  }
  .verification-title {
    font-size: 1.5rem;
  }
}
</style>
